import { useEffect, useState } from "react";
import { Barcode, Eye, FilePdf, Files, CheckCircle, XCircle, CalendarBlank } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { fmtBRL, fmtDate } from "../lib/format";
import { downloadBlob } from "../lib/download";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { downloadBoletoPdf } from "./BoletoResultDialog";
import { BoletoBaixaDialog, BoletoVencimentoDialog, SicrediSituacao } from "./BoletoActionsDialogs";

const STATUS_LABEL = { REGISTRADO: "Em carteira", PAGO: "Liquidado", BAIXADO: "Baixado" };
const STATUS_CLS = {
  REGISTRADO: "text-sky-300 bg-sky-400/10 border-sky-400/30",
  PAGO: "text-emerald-300 bg-emerald-400/10 border-emerald-400/30",
  BAIXADO: "text-zinc-400 bg-zinc-400/10 border-zinc-400/30",
};

const Stat = ({ k, title, value, cls }) => (
  <div data-testid={`boleto-group-${k}`}>
    <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">{title}</p>
    <p className={`font-semibold ${cls}`}>{fmtBRL(value)}</p>
  </div>
);

// groupId (avulso parcelado) OU orderId (parcelas de um pedido); onView abre o BoletoResultDialog de uma parcela
export const BoletoGroupDialog = ({ groupId, orderId, onClose, onView, onChanged, title = "Boletos parcelados" }) => {
  const key = groupId ? `group/${groupId}` : orderId ? `order/${orderId}` : null;
  const [g, setG] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [toBaixar, setToBaixar] = useState(null);
  const [toVencer, setToVencer] = useState(null);

  const load = () => api.get(`/boletos/${key}`).then((r) => setG(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { if (key) load(); else setG(null); }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (b, path) => {
    setBusyId(b.id);
    try {
      const { data } = await api.post(`/boletos/${b.id}/${path}`);
      toast.success(data.message || "Ok");
      load();
      onChanged?.(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const downloadAll = async () => {
    try {
      const resp = await api.get(`/boletos/${key}/pdf`, { responseType: "blob" });
      const slug = g.kind === "order" ? `pedido_${g.order_code}` : `grupo_${g.group_code}`;
      downloadBlob(resp.data, `boletos_${slug}_${(g.client_name || "").trim()}.pdf`.replace(/\s+/g, "_"));
      const n = resp.headers?.["x-boletos-count"] || g.count;
      toast.success(`PDF único com ${n} boleto(s) baixado — um por página`);
    } catch (e) {
      toast.error(apiError(e) || "Não foi possível gerar o PDF único");
    }
  };

  return (
    <Dialog open={!!key} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-[95vw] sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="boleto-group-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 flex-wrap">
            <Barcode size={20} className="text-emerald-400" /> {title}{g ? ` — ${g.set_label}` : ""}
            {g?.simulado && <span className="text-[10px] uppercase text-amber-400 border border-amber-500/40 rounded px-1.5 py-0.5">simulação</span>}
          </DialogTitle>
        </DialogHeader>
        {!g ? <p className="text-sm text-zinc-500 py-6 text-center">Carregando...</p> : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm border border-zinc-800 rounded-lg p-3">
              <div className="col-span-2 sm:col-span-1">
                <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Cliente</p>
                <p className="text-zinc-200 truncate" title={g.client_name} data-testid="boleto-group-client">{g.client_name}</p>
                {g.description && <p className="text-xs text-zinc-500 truncate" title={g.description}>{g.description}</p>}
              </div>
              <Stat k="total" title={`Total (${g.count}x)`} value={g.total} cls="text-zinc-100" />
              <Stat k="open" title="Em carteira" value={g.open} cls="text-sky-300" />
              <Stat k="paid" title="Liquidado" value={g.paid} cls="text-emerald-300" />
              <Stat k="baixado" title="Baixado" value={g.baixado} cls="text-zinc-400" />
            </div>

            <div className="border border-zinc-800 rounded-lg overflow-x-auto">
              <table className="w-full text-sm" data-testid="boleto-group-table">
                <thead className="bg-zinc-900/60 text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                  <tr>
                    <th className="text-left px-3 py-2">Parcela</th>
                    <th className="text-left px-3 py-2">Boleto</th>
                    <th className="text-left px-3 py-2">Nosso número</th>
                    <th className="text-left px-3 py-2">Vencimento</th>
                    <th className="text-right px-3 py-2">Valor</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-right px-3 py-2">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  {g.boletos.map((b) => (
                    <tr key={b.id} data-testid={`boleto-group-row-${b.code}`} className="hover:bg-zinc-900/40">
                      <td className="px-3 py-2 text-zinc-200 font-medium">{b.parcela}</td>
                      <td className="px-3 py-2 text-zinc-400">#{b.code} <span className="text-zinc-600 text-xs">· doc {b.seu_numero}</span></td>
                      <td className="px-3 py-2 text-zinc-400 font-mono text-xs">{b.nosso_numero_fmt || b.nosso_numero}</td>
                      <td className="px-3 py-2 text-zinc-300" data-testid={`boleto-group-due-${b.code}`}>{fmtDate(b.due_date)}</td>
                      <td className="px-3 py-2 text-right text-zinc-100" data-testid={`boleto-group-amount-${b.code}`}>{fmtBRL(b.amount)}</td>
                      <td className="px-3 py-2">
                        <span data-testid={`boleto-group-status-${b.code}`} className={`inline-flex px-2 py-0.5 text-xs font-medium border rounded ${STATUS_CLS[b.status] || ""}`}>{STATUS_LABEL[b.status] || b.status}</span>
                        <SicrediSituacao b={b} busy={busyId === b.id} onRefresh={() => act(b, "verificar")} />
                        {b.status === "PAGO" && b.paid_at && <span className="block text-[10px] text-zinc-500 mt-0.5">{fmtDate(b.paid_at)} · {b.paid_via}</span>}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button data-testid={`boleto-group-view-${b.code}`} onClick={() => onView?.(b)} title="Ver código de barras, linha digitável e Pix"
                          className="text-zinc-400 hover:text-indigo-400 p-1 transition-colors"><Eye size={17} /></button>
                        <button data-testid={`boleto-group-pdf-${b.code}`} onClick={() => downloadBoletoPdf(b)} title="Baixar PDF desta parcela"
                          className="text-zinc-400 hover:text-rose-400 p-1 ml-1 transition-colors"><FilePdf size={17} /></button>
                        {b.status === "REGISTRADO" && b.simulado && (
                          <button data-testid={`boleto-group-simulate-pay-${b.code}`} disabled={busyId === b.id} onClick={() => act(b, "simular-pagamento")} title="Simular pagamento"
                            className="text-zinc-400 hover:text-emerald-400 p-1 ml-1 transition-colors disabled:opacity-50"><CheckCircle size={17} /></button>
                        )}
                        {b.status === "REGISTRADO" && (
                          <button data-testid={`boleto-group-vencimento-${b.code}`} disabled={busyId === b.id} title="Alterar vencimento no Sicredi" onClick={() => setToVencer(b)}
                            className="text-zinc-400 hover:text-indigo-400 p-1 ml-1 transition-colors disabled:opacity-50"><CalendarBlank size={17} /></button>
                        )}
                        {b.status === "REGISTRADO" && (
                          <button data-testid={`boleto-group-cancel-${b.code}`} disabled={busyId === b.id} title="Baixar / cancelar boleto" onClick={() => setToBaixar(b)}
                            className="text-zinc-400 hover:text-rose-400 p-1 ml-1 transition-colors disabled:opacity-50"><XCircle size={17} /></button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="text-sm text-zinc-400 hover:text-zinc-200 px-3 py-2">Fechar</button>
              <button data-testid="boleto-group-pdf-all" onClick={downloadAll}
                className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium px-4 py-2 rounded transition-colors">
                <Files size={16} /> PDF único com todas as parcelas ({g.boletos.filter((b) => b.status !== "BAIXADO").length})
              </button>
            </div>
          </div>
        )}
      </DialogContent>
      <BoletoBaixaDialog boleto={toBaixar} onClose={() => setToBaixar(null)} onDone={(d) => { load(); if (d) onChanged?.(d); }} />
      <BoletoVencimentoDialog boleto={toVencer} onClose={() => setToVencer(null)} onDone={(d) => { load(); if (d) onChanged?.(d); }} />
    </Dialog>
  );
};
